/**
 * Shared helper for detecting and suppressing internal wiring-debt error strings
 * that must never reach user-facing surfaces.
 *
 * The pattern matches any throw string that follows the OCT-XXXX / D## convention
 * used throughout the codebase to mark temporary per-adapter wiring debt. Both
 * confirmed throw sites match:
 *   - "OCT-3356 / D23: sync_ledger.entity_id is required when creating a new
 *      ledger entry. The per-adapter wiring lands in OCT-3365; callers must pass
 *      the connection's entityId until then."
 *   - "OCT-3356 / D23: cannot resolve sync_ledger.entity_id - workspace <id>
 *      has no default entity and no connection match"
 *
 * The regex will NOT match real provider error envelopes (Xero, QuickBooks,
 * Zoho, Wafeq, etc.) because those never contain the "OCT-XXXX / D##" prefix.
 *
 * Lives in shared/ so both client-bundled (parse-push-failure.ts / sync-status-indicator.tsx)
 * and server-only (error-message.ts, execution.ts) code can import without violating
 * the hex import direction.
 *
 * @spec DOMAIN D23 - Absolute entity isolation
 */

/**
 * User-safe replacement message for all internal wiring-debt errors.
 * Must be stable — FE render and notification bodies reference this string.
 */
export const INTERNAL_WIRING_DEBT_MESSAGE =
  "Sync to accounting system is temporarily unavailable for this entity. Our team has been notified.";

/**
 * Returns true when `raw` is an internal wiring-debt throw that must not
 * reach any user-facing surface.
 *
 * Matches strings containing the "OCT-NNNN / D##" prefix convention, e.g.:
 *   "OCT-3356 / D23: sync_ledger.entity_id is required ..."
 *   "OCT-3356 / D23: cannot resolve sync_ledger.entity_id ..."
 *
 * Word-boundary anchors on both sides prevent false positives against
 * coincidental ticket references in legitimate provider error bodies.
 */
export function isInternalDebtMessage(raw: string): boolean {
  return /\bOCT-\d+\s*\/\s*D\d+\b/i.test(raw);
}
